/**
 * Celery progress polling helper — shared across pages that kick off a
 * background task and want to drive a progress bar from it.
 *
 * Polls `/api/celery/progress/<taskId>/` with exponential backoff (250 ms →
 * 60 s ceiling), invokes `onProgress` for every snapshot, and resolves with
 * the terminal `SUCCESS` / `FAILURE` snapshot.
 *
 * Aborting: pass an AbortSignal to bail out of the loop. The caller is
 * responsible for canceling the underlying task on the server if needed —
 * this helper just stops polling.
 */
import { request } from "@/api/client";
import { CeleryProgressSchema, type CeleryProgress } from "@/api/schemas/meraki";

export interface PollOptions {
  /** Initial delay between polls in ms. Default 250. */
  initialDelayMs?: number;
  /** Cap on the delay between polls. Default 60_000. */
  maxDelayMs?: number;
  /** Cancel the loop without throwing. */
  signal?: AbortSignal;
}

export async function pollCeleryTask(
  taskId: string,
  onProgress: (p: CeleryProgress) => void,
  opts: PollOptions = {},
): Promise<CeleryProgress> {
  const max = opts.maxDelayMs ?? 60_000;
  let delay = opts.initialDelayMs ?? 250;

  while (true) {
    if (opts.signal?.aborted) {
      // Mimic terminal state so callers don't need a second branch.
      return { state: "FAILURE", message: "polling aborted" };
    }
    const raw = await request<unknown>(`/celery/progress/${taskId}/`, { cacheMode: "bypass", signal: opts.signal });
    const progress = CeleryProgressSchema.parse(raw);
    onProgress(progress);
    if (progress.state === "SUCCESS" || progress.state === "FAILURE") return progress;
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay * 1.5, max);
  }
}
